import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { ContentBlock, Reference, SourceDocument, Citation } from '../types';
import { SourceDetail } from '@features/sources';
import { createSanitizedMarkup } from '@shared/utils';
import './CitedContent.css';

interface CitedContentProps {
  contentBlocks: ContentBlock[];
  references: Reference[];
}

// Convert Reference to SourceDocument for SourceDetail compatibility
function referenceToSourceDocument(ref: Reference): SourceDocument {
  return {
    documentIndex: ref.documentIndex,
    title: ref.title,
    url: ref.url,
    sourceName: ref.sourceName,
    rssDescription: ref.rssDescription || '',
  };
}

export function CitedContent({ contentBlocks, references }: CitedContentProps) {
  const [hoveredCiteIndex, setHoveredCiteIndex] = useState<number | null>(null);
  const [selectedCitation, setSelectedCitation] = useState<{
    citation: Citation;
    source: SourceDocument;
    citationNumber: number;
    citeBlockIndex: number;
    clickX: number;
    clickY: number;
  } | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [citationElement, setCitationElement] = useState<HTMLElement | null>(null);
  // Track last touch time to prevent click from firing after touch
  const lastTouchTimeRef = useRef(0);

  useEffect(() => {
    if (isClosing) {
      const timer = setTimeout(() => {
        setSelectedCitation(null);
        setCitationElement(null);
        setIsClosing(false);
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [isClosing]);

  const openCitation = (
    citation: Citation,
    textBlockIndex: number,
    buttonElement: HTMLButtonElement
  ) => {
    const ref = references.find(r => r.documentIndex === citation.documentIndex);
    if (ref) {
      const buttonRect = buttonElement.getBoundingClientRect();
      setIsClosing(false);
      setCitationElement(buttonElement);
      const clickXPos = buttonRect.left + buttonRect.width / 2;
      const isLeftSide = clickXPos < window.innerWidth / 2;
      setSelectedCitation({
        citation,
        source: referenceToSourceDocument(ref),
        citationNumber: citation.documentIndex + 1,
        citeBlockIndex: textBlockIndex,
        clickX: clickXPos,
        clickY: buttonRect.top,
      });
      window.dispatchEvent(new CustomEvent('source-detail-open', {
        detail: { side: isLeftSide ? 'left' : 'right' }
      }));
    }
  };

  const handleCitationTouchEnd = (
    citation: Citation,
    textBlockIndex: number,
    event: React.TouchEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    lastTouchTimeRef.current = Date.now();
    openCitation(citation, textBlockIndex, event.currentTarget);
  };

  const handleCitationClick = (
    citation: Citation,
    textBlockIndex: number,
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    // Ignore click if it's a ghost click following a touch event (within 500ms)
    if (Date.now() - lastTouchTimeRef.current < 500) {
      event.preventDefault();
      return;
    }
    openCitation(citation, textBlockIndex, event.currentTarget);
  };

  const handleClose = () => {
    setIsClosing(true);
    window.dispatchEvent(new Event('source-detail-close'));
  };

  const getSourceName = (documentIndex: number): string => {
    const ref = references.find(r => r.documentIndex === documentIndex);
    return ref?.sourceName || 'Unknown Source';
  };

  if (!contentBlocks || contentBlocks.length === 0) {
    return (
      <div className="cited-content">
        <div className="cited-content__text">
          No content available
        </div>
      </div>
    );
  }

  const renderContent = () => {
    return contentBlocks.map((block, index) => {
      // Handle "text" type blocks (primary format)
      if (block.type === 'text') {
        const isHighlighted =
          hoveredCiteIndex === index ||
          selectedCitation?.citeBlockIndex === index;

        // Check if this text block has citations attached
        if ('citations' in block && block.citations && block.citations.length > 0) {
          return (
            <span key={`text-${index}`}>
              <span
                className={isHighlighted ? 'cited-content__text--highlighted' : ''}
                dangerouslySetInnerHTML={createSanitizedMarkup(block.text)}
              />
              <span className="cited-content__citations">
                {block.citations.map((citation, citationIndex) => {
                  const citationNumber = citation.documentIndex + 1;
                  const isActive = selectedCitation?.citation.documentIndex === citation.documentIndex &&
                    selectedCitation?.citeBlockIndex === index;

                  return (
                    <button
                      key={citationIndex}
                      className={`cited-content__citation ${isActive ? 'cited-content__citation--active' : ''}`}
                      onClick={(e) => handleCitationClick(citation, index, e)}
                      onTouchEnd={(e) => handleCitationTouchEnd(citation, index, e)}
                      onMouseEnter={() => setHoveredCiteIndex(index)}
                      onMouseLeave={() => setHoveredCiteIndex(null)}
                      aria-label={`Citation ${citationNumber}: ${getSourceName(citation.documentIndex)}`}
                      type="button"
                    >
                      [{citationNumber}]
                    </button>
                  );
                })}
              </span>
            </span>
          );
        }

        // Plain text block without citations
        return (
          <span
            key={`text-${index}`}
            dangerouslySetInnerHTML={createSanitizedMarkup(block.text)}
          />
        );
      }

      return null;
    });
  };

  return (
    <>
      <div className="cited-content">
        <div className="cited-content__text">
          {renderContent()}
        </div>
      </div>

      {selectedCitation && createPortal(
        <SourceDetail
          citation={selectedCitation.citation}
          source={selectedCitation.source}
          citationNumber={selectedCitation.citationNumber}
          clickX={selectedCitation.clickX}
          clickY={selectedCitation.clickY}
          isClosing={isClosing}
          onClose={handleClose}
          citationElement={citationElement}
        />,
        document.body
      )}
    </>
  );
}
